import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface SessionsDetailData {
  summary: {
    unique_ips: number;
    unique_users: number;
    total_sessions: number;
    avg_sessions_per_ip: string;
  };
  ip_stats: Array<{
    ip_address: string;
    city: string;
    state: string;
    device_type: string;
    platform: string;
    total_sessions: number;
    logged_sessions: number;
    last_activity: string;
    first_activity: string;
    avg_duration_seconds: number;
    user_type: string;
    // Novos campos de dispositivo detalhado
    device_brand?: string;
    device_model?: string;
    system_version?: string;
    app_version?: string;
    device_display_name?: string;
  }>;
  hourly_distribution: Array<{
    hour: number;
    sessions: number;
    unique_ips: number;
  }>;
  top_cities: Array<{
    city: string;
    state: string;
    unique_ips: number;
    total_sessions: number;
  }>;
  device_distribution: Array<{
    device_type: string;
    unique_ips: number;
    total_sessions: number;
  }>;
  longest_sessions: Array<{
    session_id: string;
    ip_address: string;
    city: string;
    state: string;
    user_type: string;
    device_type: string;
    first_seen: string;
    last_seen: string;
    duration_seconds: number;
    // Novos campos de dispositivo detalhado
    device_brand?: string;
    device_model?: string;
    system_version?: string;
    app_version?: string;
    device_display_name?: string;
  }>;
}

interface SessionsDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  period: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B9D'];

const API_BASE = '/analytics';

const SessionsDetailModal: React.FC<SessionsDetailModalProps> = ({ isOpen, onClose, period }) => {
  const [data, setData] = useState<SessionsDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ips' | 'hourly' | 'cities' | 'devices' | 'longest'>('ips');

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, period]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`${API_BASE}/sessions-detail?period=${period}&limit=50`);
      setData(response.data.data);
    } catch (error: any) {
      console.error('Erro ao buscar detalhamento de sessões:', error);
      setError(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min`;
  };

  const formatHour = (hour: number): string => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Detalhamento de Sessões (Últimos {period} dias)</h2>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        {loading && <div style={styles.loading}>Carregando...</div>}

        {error && (
          <div style={styles.error}>
            <p>{error}</p>
            <button onClick={fetchData} style={styles.retryButton}>Tentar Novamente</button>
          </div>
        )}

        {data && !loading && (
          <>
            {/* Summary Cards */}
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryLabel}>IPs Únicos</div>
                <div style={styles.summaryValue}>{data.summary.unique_ips.toLocaleString()}</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryLabel}>Usuários Únicos</div>
                <div style={styles.summaryValue}>{data.summary.unique_users.toLocaleString()}</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryLabel}>Total Sessões</div>
                <div style={styles.summaryValue}>{data.summary.total_sessions.toLocaleString()}</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryLabel}>Sessões/IP (Média)</div>
                <div style={styles.summaryValue}>{data.summary.avg_sessions_per_ip}</div>
              </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabs}>
              <button
                style={activeTab === 'ips' ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab('ips')}
              >
                IPs ({data.ip_stats.length})
              </button>
              <button
                style={activeTab === 'hourly' ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab('hourly')}
              >
                Por Hora
              </button>
              <button
                style={activeTab === 'cities' ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab('cities')}
              >
                Cidades ({data.top_cities.length})
              </button>
              <button
                style={activeTab === 'devices' ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab('devices')}
              >
                Dispositivos
              </button>
              <button
                style={activeTab === 'longest' ? styles.tabActive : styles.tab}
                onClick={() => setActiveTab('longest')}
              >
                Maior Duração
              </button>
            </div>

            {/* Tab Content */}
            <div style={styles.tabContent}>
              {/* IPs Tab */}
              {activeTab === 'ips' && (
                <div style={styles.tableWrapper}>
                  <h3 style={styles.subtitle}>Top 50 IPs com Mais Sessões</h3>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>IP</th>
                        <th style={styles.th}>Cidade</th>
                        <th style={styles.th}>Estado</th>
                        <th style={styles.th}>Dispositivo</th>
                        <th style={styles.th}>Platform</th>
                        <th style={styles.th}>Sessões</th>
                        <th style={styles.th}>Logadas</th>
                        <th style={styles.th}>Tipo</th>
                        <th style={styles.th}>Última Atividade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ip_stats.map((ip, index) => (
                        <tr key={index} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}>{ip.ip_address}</td>
                          <td style={styles.td}>{ip.city || 'N/A'}</td>
                          <td style={styles.td}>{ip.state || 'N/A'}</td>
                          <td style={styles.td}>
                            {ip.device_display_name ? (
                              <span title={ip.device_display_name} style={styles.deviceDetailBadge}>
                                {ip.device_brand && ip.device_model
                                  ? `${ip.device_brand} ${ip.device_model}`
                                  : ip.device_display_name}
                                {ip.system_version && (
                                  <span style={styles.versionBadge}>v{ip.system_version}</span>
                                )}
                              </span>
                            ) : (
                              <span style={styles.deviceTypeBadge}>
                                {ip.device_type === 'ios' ? '📱 iOS' :
                                 ip.device_type === 'android' ? '📱 Android' :
                                 ip.device_type === 'ios-tablet' ? '📲 iPad' :
                                 ip.device_type === 'android-tablet' ? '📲 Android Tablet' :
                                 ip.device_type === 'desktop' ? '💻 Desktop' :
                                 ip.device_type === 'mobile-unknown' ? '📱 Mobile' :
                                 ip.device_type}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>{ip.platform}</td>
                          <td style={styles.td}>{ip.total_sessions}</td>
                          <td style={styles.td}>{ip.logged_sessions}</td>
                          <td style={styles.td}>
                            {ip.user_type && ip.user_type !== 'guest' ? (
                              <span style={styles.userTypeBadge}>
                                {ip.user_type === 'banda' ? '🎸 Banda' :
                                 ip.user_type === 'empresa' ? '🏢 Empresa' :
                                 ip.user_type === 'usuario' ? '👤 Usuário' :
                                 ip.user_type}
                              </span>
                            ) : (
                              <span style={{...styles.userTypeBadge, color: '#999'}}>-</span>
                            )}
                          </td>
                          <td style={styles.td}>{format(new Date(ip.last_activity), 'dd/MM HH:mm', { locale: ptBR })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Hourly Tab */}
              {activeTab === 'hourly' && (
                <div>
                  <h3 style={styles.subtitle}>Distribuição de Sessões por Hora do Dia</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data.hourly_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tickFormatter={formatHour} />
                      <YAxis />
                      <Tooltip
                        labelFormatter={formatHour}
                        formatter={(value: any, name: string) => [
                          value.toLocaleString(),
                          name === 'sessions' ? 'Sessões' : 'IPs Únicos'
                        ]}
                      />
                      <Legend formatter={(value) => value === 'sessions' ? 'Sessões' : 'IPs Únicos'} />
                      <Bar dataKey="sessions" fill="#8884d8" name="Sessões" />
                      <Bar dataKey="unique_ips" fill="#82ca9d" name="IPs Únicos" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Cities Tab */}
              {activeTab === 'cities' && (
                <div style={styles.tableWrapper}>
                  <h3 style={styles.subtitle}>Top 20 Cidades</h3>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Cidade</th>
                        <th style={styles.th}>Estado</th>
                        <th style={styles.th}>IPs Únicos</th>
                        <th style={styles.th}>Total de Sessões</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_cities.map((city, index) => (
                        <tr key={index} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}>{city.city}</td>
                          <td style={styles.td}>{city.state}</td>
                          <td style={styles.td}>{city.unique_ips}</td>
                          <td style={styles.td}>{city.total_sessions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Devices Tab */}
              {activeTab === 'devices' && (
                <div>
                  <h3 style={styles.subtitle}>Distribuição por Tipo de Dispositivo</h3>
                  <div style={styles.twoColumnGrid}>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={data.device_distribution}
                          dataKey="total_sessions"
                          nameKey="device_type"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={(entry: any) => entry.device_type}
                        >
                          {data.device_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={styles.deviceTable}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>Device</th>
                            <th style={styles.th}>IPs</th>
                            <th style={styles.th}>Sessões</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.device_distribution.map((device, index) => (
                            <tr key={index} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                              <td style={styles.td}>
                                <span style={{ ...styles.deviceColor, backgroundColor: COLORS[index % COLORS.length] }}></span>
                                {device.device_type}
                              </td>
                              <td style={styles.td}>{device.unique_ips}</td>
                              <td style={styles.td}>{device.total_sessions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Longest Sessions Tab */}
              {activeTab === 'longest' && (
                <div style={styles.tableWrapper}>
                  <h3 style={styles.subtitle}>Top 20 Sessões com Maior Duração</h3>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>IP</th>
                        <th style={styles.th}>Cidade</th>
                        <th style={styles.th}>Tipo</th>
                        <th style={styles.th}>Dispositivo</th>
                        <th style={styles.th}>Início</th>
                        <th style={styles.th}>Fim</th>
                        <th style={styles.th}>Duração</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.longest_sessions.map((session, index) => (
                        <tr key={session.session_id} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}>{session.ip_address}</td>
                          <td style={styles.td}>{session.city || 'N/A'}</td>
                          <td style={styles.td}>{session.user_type}</td>
                          <td style={styles.td}>
                            {session.device_display_name ? (
                              <span title={session.device_display_name} style={styles.deviceDetailBadge}>
                                {session.device_brand && session.device_model
                                  ? `${session.device_brand} ${session.device_model}`
                                  : session.device_display_name}
                                {session.system_version && (
                                  <span style={styles.versionBadge}>v{session.system_version}</span>
                                )}
                              </span>
                            ) : (
                              <span style={styles.deviceTypeBadge}>
                                {session.device_type === 'ios' ? '📱 iOS' :
                                 session.device_type === 'android' ? '📱 Android' :
                                 session.device_type === 'ios-tablet' ? '📲 iPad' :
                                 session.device_type === 'android-tablet' ? '📲 Android Tablet' :
                                 session.device_type === 'desktop' ? '💻 Desktop' :
                                 session.device_type === 'mobile-unknown' ? '📱 Mobile' :
                                 session.device_type}
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>{format(new Date(session.first_seen), 'dd/MM HH:mm', { locale: ptBR })}</td>
                          <td style={styles.td}>{format(new Date(session.last_seen), 'dd/MM HH:mm', { locale: ptBR })}</td>
                          <td style={styles.td}><strong>{formatDuration(session.duration_seconds)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '20px',
  } as React.CSSProperties,
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '1400px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 30px',
    borderBottom: '2px solid #eee',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 10,
  } as React.CSSProperties,
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  } as React.CSSProperties,
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background 0.2s',
  } as React.CSSProperties,
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '15px',
    padding: '20px 30px',
    borderBottom: '1px solid #eee',
  } as React.CSSProperties,
  summaryCard: {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    textAlign: 'center',
  } as React.CSSProperties,
  summaryLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    textTransform: 'uppercase',
  } as React.CSSProperties,
  summaryValue: {
    fontSize: '26px',
    fontWeight: 'bold',
    color: '#1976d2',
  } as React.CSSProperties,
  tabs: {
    display: 'flex',
    borderBottom: '2px solid #eee',
    padding: '0 30px',
    gap: '5px',
  } as React.CSSProperties,
  tab: {
    padding: '12px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s',
  } as React.CSSProperties,
  tabActive: {
    padding: '12px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1976d2',
    borderBottom: '3px solid #1976d2',
  } as React.CSSProperties,
  tabContent: {
    padding: '20px 30px',
  } as React.CSSProperties,
  subtitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px',
  } as React.CSSProperties,
  tableWrapper: {
    overflowX: 'auto',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  } as React.CSSProperties,
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #ddd',
    backgroundColor: '#f9f9f9',
    fontWeight: 'bold',
    color: '#333',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
  } as React.CSSProperties,
  trEven: {
    backgroundColor: '#fafafa',
  } as React.CSSProperties,
  trOdd: {
    backgroundColor: 'white',
  } as React.CSSProperties,
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    alignItems: 'start',
  } as React.CSSProperties,
  deviceTable: {
    overflowX: 'auto',
  } as React.CSSProperties,
  deviceColor: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    marginRight: '8px',
  } as React.CSSProperties,
  userTypeBadge: {
    fontSize: '13px',
    color: '#333',
    fontWeight: '500',
  } as React.CSSProperties,
  deviceDetailBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#333',
    fontWeight: '500',
  } as React.CSSProperties,
  deviceTypeBadge: {
    fontSize: '13px',
    color: '#666',
  } as React.CSSProperties,
  versionBadge: {
    fontSize: '11px',
    color: '#888',
    backgroundColor: '#f0f0f0',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '4px',
  } as React.CSSProperties,
  loading: {
    padding: '60px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#666',
  } as React.CSSProperties,
  error: {
    padding: '60px',
    textAlign: 'center',
    color: '#d32f2f',
  } as React.CSSProperties,
  retryButton: {
    marginTop: '15px',
    padding: '10px 20px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
  } as React.CSSProperties,
};

export default SessionsDetailModal;
